import { useMemo } from 'react';

import type { useCart, useCustomerCredit } from '@order/hooks';
import { useFeatureFlag } from '@order/LaunchDarkly';

import type { useCheckoutPayload } from '../useCheckoutPayload';

/**
 * The customer can toggle the usage of credit in the checkout.
 * That is handled by the backend and will always use as much credit as possible.
 * This hook is only to show in the frontend the correct amount of credit that will be used.
 *
 * If there is more credit than the order cost, use the order cost.
 * If the order costs more than the available credit, use the credit amount.
 */

export const useCheckoutCredit = (props: CheckoutCreditProps) => {
  const { total, isCartDelivery, isCartPickup, availableCredit, payload } =
    props;

  const { useCredit, deliveryOrderDetail, tip: orderPickupTip } = payload;

  const shouldEnablePickupTipping = useFeatureFlag(
    'cels-754-enable-tipping-native-pickup',
  );

  const pickupTip = shouldEnablePickupTipping ? orderPickupTip : 0;
  const deliveryTip = deliveryOrderDetail?.tip;
  const tip = isCartDelivery ? deliveryTip : isCartPickup ? pickupTip : 0;

  const checkoutCredit = useMemo(() => {
    if (!useCredit) return 0;

    const amountToPay = (total ?? 0) + (tip ?? 0);
    const amountToCredit = availableCredit ?? 0;
    const appliedCredit = Math.min(amountToPay, amountToCredit);

    return appliedCredit;
  }, [total, availableCredit, tip, useCredit]);

  return { checkoutCredit };
};

type CheckoutCreditProps = Pick<
  ReturnType<typeof useCart> &
    ReturnType<typeof useCheckoutPayload> &
    ReturnType<typeof useCustomerCredit>,
  'total' | 'isCartDelivery' | 'isCartPickup' | 'availableCredit' | 'payload'
>;
